import { useState, useRef, useCallback, useEffect } from "react";
import {
  ChargeType,
  PricingModel,
  PricingTableRow,
  Pricing,
  UseServiceItemFormProps,
  ServiceItemFormState,
  UseServiceItemFormReturn,
  FormValidData,
  FormSetValueInterface,
  ServiceItemApiPayload,
  ServiceDuration,
  BillingFrequency
} from "../types";
import {
  convertPricingTableRowToPricing,
  getChargeTypeFromNumber,
  getChargeTypeNumber,
  getPricingModelNumber,
  initializeServiceItemFormData
} from "../utils";
import {
  useCreateServiceItem,
  useUpdateServiceItem,
  useUpdateServiceItemPricing,
  useCreateServiceItemPricing,
  useServiceItemTypes
} from "../hooks";

export const useServiceItemForm = ({
  serviceItem,
  productId,
  isEditMode = false
}: UseServiceItemFormProps): UseServiceItemFormReturn => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formSetValue, setFormSetValue] =
    useState<FormSetValueInterface | null>(null);
  const [currentFormData, setCurrentFormData] = useState<FormValidData | null>(
    null
  );
  const initializedRef = useRef(false);
  const serviceItemIdRef = useRef<string | null>(null);
  const originalPricingRef = useRef<PricingTableRow[]>([]);
  const originalOneTimePriceRef = useRef<number>(0);
  // Use ref to track current charge type to avoid infinite loops in handleFormValid
  const currentChargeTypeRef = useRef<ChargeType>(ChargeType.RECURRING);

  const [formState, setFormState] = useState<ServiceItemFormState>(() =>
    initializeServiceItemFormData(serviceItem || null)
  );

  const createServiceItemMutation = useCreateServiceItem();
  const updateServiceItemMutation = useUpdateServiceItem();
  const updateServiceItemPricingMutation = useUpdateServiceItemPricing();
  const createServiceItemPricingMutation = useCreateServiceItemPricing();
  const { data: serviceItemTypes = [] } = useServiceItemTypes();

  useEffect(() => {
    if (serviceItem?.id !== serviceItemIdRef.current) {
      serviceItemIdRef.current = serviceItem?.id || null;
      initializedRef.current = false;
      const initialState = initializeServiceItemFormData(serviceItem || null);
      setFormState(initialState);
      originalPricingRef.current = initialState.pricing;
      originalOneTimePriceRef.current = initialState.oneTimePrice;
      currentChargeTypeRef.current = initialState.chargeType;
    }
  }, [serviceItem]);

  useEffect(() => {
    if (
      formSetValue?.setValue &&
      typeof formSetValue.setValue === "function" &&
      serviceItem &&
      isEditMode &&
      !initializedRef.current
    ) {
      initializedRef.current = true;
      // Use setTimeout to ensure form is fully initialized
      setTimeout(() => {
        try {
          formSetValue.setValue("name", serviceItem.name);
          formSetValue.setValue("description", serviceItem.description || "");
          formSetValue.setValue("typeId", serviceItem.typeId);
          formSetValue.setValue("pricingModel", serviceItem.pricingModel);

          const chargeType = getChargeTypeFromNumber(
            serviceItem.chargeType as unknown as number
          );
          formSetValue.setValue("chargeType", String(chargeType));

          if (
            Number(chargeType) === ChargeType.ONE_TIME &&
            serviceItem.pricing.length > 0
          ) {
            formSetValue.setValue(
              "oneTimePrice",
              serviceItem.pricing[0].price ?? 0
            );
          }
        } catch (error) {
          console.error("Error setting form values:", error);
        }
      }, 100);
    }
  }, [formSetValue, serviceItem, isEditMode]);

  const setPricing = useCallback((pricing: PricingTableRow[]) => {
    setFormState((prev) => ({ ...prev, pricing }));
  }, []);

  const setChargeType = useCallback((chargeType: ChargeType) => {
    const numericChargeType = Number(chargeType) as ChargeType;
    currentChargeTypeRef.current = numericChargeType;

    setFormState((prev) => {
      if (Number(numericChargeType) !== Number(prev.chargeType)) {
        if (Number(numericChargeType) === ChargeType.ONE_TIME) {
          originalPricingRef.current = prev.pricing;
          return {
            ...prev,
            chargeType: numericChargeType,
            pricing: [],
            oneTimePrice: originalOneTimePriceRef.current
          };
        } else {
          originalOneTimePriceRef.current = prev.oneTimePrice;
          return {
            ...prev,
            chargeType: numericChargeType,
            pricing: originalPricingRef.current,
            oneTimePrice: 0
          };
        }
      }
      return { ...prev, chargeType: numericChargeType };
    });
  }, []);

  const setOneTimePrice = useCallback((oneTimePrice: number) => {
    setFormState((prev) => ({ ...prev, oneTimePrice }));
  }, []);

  const handleFormValid = useCallback(
    (valid: boolean, data: FormValidData | null) => {
      setIsFormValid(valid);
      // Treat the very first call (currentFormData === null) as initial load,
      // not a type change. Otherwise edit mode would auto-fill chargeType /
      // pricingModel from the type's defaults and override the saved values.
      const typeIdChanged =
        currentFormData !== null &&
        data?.typeId &&
        data.typeId !== currentFormData.typeId;

      if (
        typeIdChanged &&
        formSetValue?.setValue &&
        typeof formSetValue.setValue === "function" &&
        serviceItemTypes.length > 0
      ) {
        const selectedType = serviceItemTypes.find(
          (type) => type.id === data.typeId
        );

        if (selectedType) {
          const rawChargeType = selectedType.defaultChargeType;
          if (rawChargeType !== undefined && rawChargeType !== null) {
            const chargeTypeEnum =
              typeof rawChargeType === "number"
                ? getChargeTypeFromNumber(rawChargeType)
                : Number(rawChargeType);
            if (chargeTypeEnum) {
              formSetValue.setValue("chargeType", String(chargeTypeEnum));
              setChargeType(chargeTypeEnum as ChargeType);
            }
          }

          const rawPricingModel = selectedType.defaultPricingModels;
          if (rawPricingModel !== undefined && rawPricingModel !== null) {
            formSetValue.setValue("pricingModel", rawPricingModel);
          }
        }
      }

      setCurrentFormData(data);

      if (data) {
        const { chargeType, ...formDataWithoutChargeType } = data;
        setFormState((prev) => ({
          ...prev,
          formData: {
            ...prev.formData,
            ...formDataWithoutChargeType
          }
        }));
      }

      if (
        data?.chargeType !== undefined &&
        data?.chargeType !== null &&
        Number(data.chargeType) !== Number(currentChargeTypeRef.current)
      ) {
        setChargeType(Number(data.chargeType) as ChargeType);
      }
    },
    [setChargeType, currentFormData?.typeId, formSetValue, serviceItemTypes]
  );

  const isSubmitting = isEditMode
    ? updateServiceItemMutation.isPending ||
      updateServiceItemPricingMutation.isPending ||
      createServiceItemPricingMutation.isPending
    : createServiceItemMutation.isPending;

  const hasOnlyPricingChanged = useCallback(() => {
    if (!serviceItem || !isEditMode) return false;

    const currentName = currentFormData?.name || formState.formData.name || "";
    const currentDescription =
      currentFormData?.description || formState.formData.description || "";
    const currentTypeId =
      currentFormData?.typeId || formState.formData.typeId || "";
    const currentPricingModel =
      currentFormData?.pricingModel || formState.formData.pricingModel;
    const currentChargeType = formState.chargeType;

    const originalChargeType = getChargeTypeFromNumber(
      serviceItem.chargeType as unknown as number
    );

    // Check if non-pricing fields are unchanged
    const nameUnchanged = currentName === serviceItem.name;
    const descriptionUnchanged =
      currentDescription === (serviceItem.description || "");
    const typeIdUnchanged = currentTypeId === serviceItem.typeId;
    const pricingModelUnchanged =
      currentPricingModel === serviceItem.pricingModel;
    const chargeTypeUnchanged =
      Number(currentChargeType) === Number(originalChargeType);

    return (
      nameUnchanged &&
      descriptionUnchanged &&
      typeIdUnchanged &&
      pricingModelUnchanged &&
      chargeTypeUnchanged
    );
  }, [serviceItem, isEditMode, currentFormData, formState]);

  // Determine if updateOnly should be true (only name/description changed)
  // or false (type/chargeType/pricingModel changed)
  const getUpdateOnlyFlag = useCallback(() => {
    if (!serviceItem || !isEditMode) return false;

    const currentTypeId =
      currentFormData?.typeId || formState.formData.typeId || "";
    const currentPricingModel =
      currentFormData?.pricingModel || formState.formData.pricingModel;
    const currentChargeType = formState.chargeType;

    const originalChargeType = getChargeTypeFromNumber(
      serviceItem.chargeType as unknown as number
    );

    // Check if type/chargeType/pricingModel changed
    const typeIdChanged = Number(currentTypeId) !== serviceItem.typeId;
    const pricingModelChanged =
      currentPricingModel !== serviceItem.pricingModel;
    const chargeTypeChanged =
      Number(currentChargeType) !== Number(originalChargeType);

    // If any of type/chargeType/pricingModel changed, updateOnly is false
    if (typeIdChanged || chargeTypeChanged || pricingModelChanged) {
      return false;
    }

    // Otherwise, only name/description changed, updateOnly is true
    return true;
  }, [serviceItem, isEditMode, currentFormData, formState]);

  const isPricingValid = (data?: FormValidData | null) => {
    const currentPricingModel =
      data?.pricingModel || formState.formData.pricingModel;
    // Coerce to number: the form's singleSelect value can arrive as a string
    // depending on how the API serializes option ids, which would break a
    // strict === against PricingModel.FLEXIBLE.
    if (Number(currentPricingModel) === PricingModel.FLEXIBLE) {
      return true;
    }

    if (Number(formState.chargeType) === ChargeType.ONE_TIME) {
      const price = data?.oneTimePrice || formState.oneTimePrice;
      return price > 0;
    }
    return (
      formState.pricing.length > 0 &&
      formState.pricing.every((row) => {
        if (!row.serviceItemPeriods) {
          return false;
        }

        if (Number(row.serviceItemPeriods) === ServiceDuration.CUSTOM) {
          return true;
        }
        if (!row.billingFrequency) {
          return false;
        }
        if (Number(row.billingFrequency) === BillingFrequency.CUSTOM) {
          return true;
        }
        return row.price > 0;
      })
    );
  };

  const handleFormSubmit = useCallback(async () => {
    if (isSubmitting) {
      throw new Error("Form is already being submitted");
    }

    if (!isPricingValid(currentFormData)) {
      throw new Error("Invalid pricing data");
    }

    let pricingData: Pricing[];

    if (Number(formState.chargeType) === ChargeType.ONE_TIME) {
      const existingPricingId =
        serviceItem && serviceItem.pricing.length > 0
          ? serviceItem.pricing[0].id
          : "";
      const pricingRow: Pricing = {
        id: existingPricingId,
        serviceItemPeriods: null,
        billingFrequency: null,
        price:
          Number(currentFormData?.oneTimePrice || formState.oneTimePrice) || 0,
        convertedPrice: null
      };

      pricingData = [pricingRow];
    } else {
      const existingPricingIds = new Set(
        serviceItem ? serviceItem.pricing.map((pricingItem) => pricingItem.id) : []
      );
      pricingData = formState.pricing.map((row) => {
        const convertedPricing = convertPricingTableRowToPricing(row);
        const pricingRow: Pricing = {
          id: existingPricingIds.has(row.id) ? row.id : "",
          serviceItemPeriods: convertedPricing.serviceItemPeriods,
          billingFrequency: convertedPricing.billingFrequency,
          price: convertedPricing.price,
          convertedPrice: convertedPricing.convertedPrice
        };

        return pricingRow;
      });
    }

    const pricingModelValue =
      currentFormData?.pricingModel ||
      formState.formData.pricingModel ||
      PricingModel.PER_UNIT;
    const chargeTypeValue = formState.chargeType;

    const chargeTypeNumber = getChargeTypeNumber(chargeTypeValue);
    const pricingModelNumber = getPricingModelNumber(pricingModelValue);

    const apiPayload: ServiceItemApiPayload = {
      name: currentFormData?.name || formState.formData.name || "",
      description:
        currentFormData?.description || formState.formData.description || "",
      typeId: Number(currentFormData?.typeId || formState.formData.typeId || 0),
      pricingModel: pricingModelNumber,
      chargeType: chargeTypeNumber,
      pricing: pricingData
    };

    try {
      if (isEditMode && serviceItem) {
        // Use pricing-only endpoint if only pricing has changed.
        if (hasOnlyPricingChanged()) {
          if (serviceItem.pricing.length === 0) {
            const pricingWithoutIds = pricingData.map(
              ({ id: _id, ...rest }) => rest
            );
            return await createServiceItemPricingMutation.mutateAsync({
              pathParams: {
                productId,
                serviceItemId: serviceItem.id
              },
              data: { pricing: pricingWithoutIds }
            });
          }
          return await updateServiceItemPricingMutation.mutateAsync({
            pathParams: {
              productId,
              serviceItemId: serviceItem.id
            },
            data: { pricing: pricingData }
          });
        }
        // Add updateOnly flag: true if only name/description changed, false if type/chargeType/pricingModel changed
        const updateOnlyFlag = getUpdateOnlyFlag();
        return await updateServiceItemMutation.mutateAsync({
          pathParams: {
            productId,
            serviceItemId: serviceItem.id
          },
          data: { ...apiPayload, updateOnly: updateOnlyFlag }
        });
      } else {
        return await createServiceItemMutation.mutateAsync({
          pathParams: { productId },
          data: {
            ...apiPayload,
            pricing: apiPayload.pricing.map(({ id: _id, ...rest }) => rest)
          }
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      throw error;
    }
  }, [
    formState,
    currentFormData,
    serviceItem,
    productId,
    isEditMode,
    createServiceItemMutation,
    updateServiceItemMutation,
    updateServiceItemPricingMutation,
    createServiceItemPricingMutation,
    isSubmitting,
    isPricingValid,
    hasOnlyPricingChanged,
    getUpdateOnlyFlag
  ]);

  const handleSubmitButton = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  return {
    formState,
    isFormValid: isFormValid && isPricingValid(currentFormData),
    formRef,
    formSetValue,
    setFormSetValue,
    handleFormValid,
    handleFormSubmit,
    handleSubmitButton,
    isSubmitting,
    setPricing,
    setChargeType,
    setOneTimePrice,
    isUpdateOnly: getUpdateOnlyFlag
  };
};
